import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/common/Layout';
import ListPagination from '../components/common/ListPagination';
import { leadService, CreateLeadData, UpdateLeadData, LeadFilters, LeadAssignmentInput } from '../services/leadService';
import { exportService } from '../services/exportService';
import { Lead, LeadAssignmentRow } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { useAppData } from '../context/AppDataContext';
import { useAuth } from '../context/AuthContext';
import { FiPhone, FiCalendar, FiUserMinus, FiCheckCircle, FiClock, FiDownload, FiMessageSquare, FiX, FiEdit2, FiTrash2, FiMail } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import { formatDate, formatDateTime, getTodayISO, getNowTimeHHMM, normalizeCallDateRange, datetimeLocalToMysql } from '../utils/dateUtils';
import { callLogService, CreateCallLogData } from '../services/callLogService';
import { CallLog } from '../types';
import { useCallOutcomeOptions } from '../hooks/useCallOutcomeOptions';
import CallStatusSelect from '../components/calls/CallStatusSelect';
import PriorCallAlert from '../components/calls/PriorCallAlert';
import AllCallLogButton from '../components/calls/AllCallLogButton';
import { getSettingColorClass } from '../utils/settingColors';
import { useListUrlState } from '../hooks/useListUrlState';
import { ListFilterSpec } from '../utils/listQueryParams';

const LEAD_LIST_FILTER_SPEC: ListFilterSpec<LeadFilters> = {
  strings: ['status', 'source', 'last_call_outcome', 'project_district', 'conversion_status', 'search', 'next_call_due', 'call_date_from', 'call_date_to'],
  numbers: ['assigned_to', 'brand_id'],
  booleans: ['source_empty', 'ready_for_conversion'],
};

/** Strip trailing " with <number> (<...>)" from party name so listing shows only the name. */
function displayPartyName(partyName: string | undefined): string {
  if (!partyName) return '';
  return partyName.replace(/\s+with\s+\d+\s*\([^)]*\)\s*$/i, '').trim();
}

function leadHasAnyConversion(lead: Lead): boolean {
  const n = lead.conversion_count;
  if (typeof n === 'number' && n > 0) return true;
  return lead.conversion_status === 'converted';
}

/** Per-assignment status (lead_assignments.status); same values as list filter. */
const LEAD_ASSIGNMENT_STATUS_OPTIONS: NonNullable<LeadAssignmentInput['status']>[] = [
  'PROSPECT',
  'INTERSTED',
  'NOT INTERSTED',
  'EXISTING OTHERS',
  'EXISTING INFRASOL',
  'customer'
];

function defaultLeadAssignmentRow(): LeadAssignmentInput {
  return { brand_id: undefined as any, user_id: 0, status: 'PROSPECT' };
}

/** Tooltip for Converted badge: customer + sales officer (assignee) vs who recorded conversion. */
function conversionBadgeTooltip(lead: Lead, assignment: { brand_id: number | null; user_id: number }): string {
  const bid = assignment.brand_id;
  const conv = (lead.conversions || []).find(
    (c) =>
      ((c.brand_id === null || c.brand_id === undefined) && bid === null) ||
      (c.brand_id != null && bid !== null && c.brand_id === bid)
  );
  if (!conv) return 'Converted';
  const code = conv.customer_code ? `${conv.customer_code}` : `ID ${conv.customer_id}`;
  const so =
    conv.customer_sales_officer_name ||
    (conv.customer_sales_officer_id != null ? `User #${conv.customer_sales_officer_id}` : '');
  const by = conv.converted_by_name || (conv.converted_by != null ? `User #${conv.converted_by}` : '');
  const parts = [`Customer ${code}`];
  if (so) parts.push(`Sales officer: ${so}`);
  if (by && conv.converted_by != null && conv.customer_sales_officer_id != null && conv.converted_by !== conv.customer_sales_officer_id) {
    parts.push(`Recorded by ${by}`);
  }
  return parts.join(' · ');
}

function syntheticAssignmentConversionFlags(
  lead: Lead,
  brandId: number | null,
  userId: number
): { brand_converted: boolean; converted_by_this_user: boolean } {
  const convs = lead.conversions || [];
  let brandConverted = false;
  let convertedByThisUser = false;
  for (const c of convs) {
    const brandMatch =
      (c.brand_id == null && brandId === null) ||
      (c.brand_id != null && brandId !== null && c.brand_id === brandId);
    if (!brandMatch) continue;
    brandConverted = true;
    if (userId > 0) {
      const so = c.customer_sales_officer_id;
      if (so != null && so === userId) convertedByThisUser = true;
      else if ((so == null || so === undefined) && c.converted_by === userId) convertedByThisUser = true;
    }
  }
  return { brand_converted: brandConverted, converted_by_this_user: convertedByThisUser };
}

const Leads: React.FC = () => {
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { options: callOutcomeOptions, loading: callOutcomeOptionsLoading } = useCallOutcomeOptions();
  const { users, brands, refreshUsers, refreshBrands } = useAppData();
  const canUseBulkActions = user?.role === 'org_admin' || user?.role === 'manager';
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [expandedLeadIds, setExpandedLeadIds] = useState<Record<number, boolean>>({});
  const listRestoreFromNav = (location.state as { restore?: { page?: number; perPage?: number; filters?: LeadFilters } } | undefined)?.restore;
  const { page, setPage, perPage, setPerPage, filters, setFilters } = useListUrlState<LeadFilters>({
    spec: LEAD_LIST_FILTER_SPEC,
    restore: listRestoreFromNav,
  });
  const [leadListFilterOptions, setLeadListFilterOptions] = useState<{ sources: string[]; call_outcomes: string[] }>({
    sources: [],
    call_outcomes: []
  });
  const [searchTerm, setSearchTerm] = useState(() => {
    const s = listRestoreFromNav?.filters?.search ?? filters.search;
    return typeof s === 'string' ? s : '';
  });
  const [total, setTotal] = useState(0);
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState<'delete' | 'assign' | 'unassign' | 'status' | 'source'>('delete');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkAssignments, setBulkAssignments] = useState<LeadAssignmentInput[]>([{ brand_id: undefined as any, user_id: 0 }]);
  const [bulkStatus, setBulkStatus] = useState<LeadAssignmentInput['status']>('PROSPECT');
  const [bulkSource, setBulkSource] = useState('');
  const [formData, setFormData] = useState<CreateLeadData>({
    party_name: '',
    contact_no: '',
    secondary_contact_no: '',
    secondary_contact_person: '',
    email: '',
    assignments: [defaultLeadAssignmentRow()]
  });
  const isInitialMount = useRef(true);
  const hasLoadedRef = useRef(false);
  const leadsFetchGen = useRef(0);
  const [callLogModalLead, setCallLogModalLead] = useState<Lead | null>(null);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [callLogLoading, setCallLogLoading] = useState(false);
  const [showCallLogForm, setShowCallLogForm] = useState(false);
  const [callFormData, setCallFormData] = useState<CreateCallLogData>({
    entity_type: 'lead',
    entity_id: 0,
    call_date: getTodayISO(),
    call_time: getNowTimeHHMM(),
    call_type: 'outbound'
  });

  // Refresh context data on page visit and load leads list
  useEffect(() => {
    // Prevent duplicate calls from React StrictMode
    // Use a combination: check ref AND if we're not currently loading
    if (hasLoadedRef.current) {
      return;
    }
    
    // Set ref immediately to prevent duplicate calls
    hasLoadedRef.current = true;

    const loadData = async () => {
      await refreshUsers();
      await refreshBrands();
      try {
        const opts = await leadService.getFilterOptions();
        setLeadListFilterOptions({ sources: opts.sources || [], call_outcomes: opts.call_outcomes || [] });
      } catch {
        setLeadListFilterOptions({ sources: [], call_outcomes: [] });
      }
      await loadLeads();
      isInitialMount.current = false;
    };
    
    loadData();
  }, []);

  // Reload leads when filters, page, or perPage change (but not on initial mount)
  useEffect(() => {
    if (!isInitialMount.current) {
      setExpandedLeadIds({});
      setSelectedLeadIds([]);
      loadLeads();
    }
  }, [filters, page, perPage]);

  // Load call logs when Call Log modal opens
  useEffect(() => {
    if (!callLogModalLead) {
      setCallLogs([]);
      return;
    }
    let cancelled = false;
    setCallLogLoading(true);
    const params = callLogModalLead.assigned_to ? { called_by: callLogModalLead.assigned_to } : undefined;
    callLogService.getByEntity('lead', callLogModalLead.id, params)
      .then((data) => { if (!cancelled) setCallLogs(data); })
      .catch(() => { if (!cancelled) setCallLogs([]); })
      .finally(() => { if (!cancelled) setCallLogLoading(false); });
    return () => { cancelled = true; };
  }, [callLogModalLead?.id, callLogModalLead?.assigned_to]);

  const openCallLogModal = (lead: Lead, assignment?: LeadAssignmentRow) => {
    const modalLead: Lead = assignment
      ? {
          ...lead,
          assigned_to: assignment.user_id,
          assigned_to_name: assignment.assigned_to_name,
          assignment_id: assignment.id,
          brand_id: assignment.brand_id,
          brand_name: assignment.brand_name,
          status: assignment.status as any,
          last_call_date: assignment.last_call_date ?? undefined,
          next_call_date: assignment.next_call_date ?? undefined,
          total_calls: assignment.total_calls
        }
      : lead;

    setCallLogModalLead(modalLead);
    setShowCallLogForm(false);
    setCallFormData({
      entity_type: 'lead',
      entity_id: lead.id,
      lead_assignment_id: modalLead.assignment_id ?? undefined,
      call_date: getTodayISO(),
      call_time: getNowTimeHHMM(),
      call_type: 'outbound'
    });
  };

  const closeCallLogModal = () => {
    setCallLogModalLead(null);
    setShowCallLogForm(false);
  };

  const loadCallLogsInModal = () => {
    if (callLogModalLead) {
      const params = callLogModalLead.assigned_to ? { called_by: callLogModalLead.assigned_to } : undefined;
      callLogService.getByEntity('lead', callLogModalLead.id, params).then(setCallLogs);
    }
  };

  const handleCallLogSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await callLogService.create(callFormData);
      setShowCallLogForm(false);
      setCallFormData({
        entity_type: 'lead',
        entity_id: callLogModalLead!.id,
        lead_assignment_id: callLogModalLead!.assignment_id ?? undefined,
        call_date: getTodayISO(),
        call_time: getNowTimeHHMM(),
        call_type: 'outbound'
      });
      loadCallLogsInModal();
      await loadLeads();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to log call');
    }
  };

  const getCallOutcomeColor = (outcome?: string) => {
    switch (outcome) {
      case 'interested': return 'bg-green-100 text-green-800';
      case 'not_interested': return 'bg-red-100 text-red-800';
      case 'callback_requested': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const loadLeads = async () => {
    const gen = ++leadsFetchGen.current;
    try {
      setLoading(true);
      const result = await leadService.getAll({ ...filters, page, per_page: perPage });
      if (gen !== leadsFetchGen.current) return;
      setLeads(result.data);
      setTotal(result.total);
      setError('');
    } catch (err: any) {
      if (gen !== leadsFetchGen.current) return;
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      if (gen === leadsFetchGen.current) {
        setLoading(false);
      }
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setModalError('');
    const validAssignments = (formData.assignments || []).filter((a) => a.user_id && a.user_id > 0);
    if (validAssignments.length === 0) {
      setModalError('At least one assignment (brand and user) is required');
      return;
    }
    const { status: _omitStatus, assigned_to: _omitAssigned, ...restForm } = formData;
    const payload: CreateLeadData = {
      ...restForm,
      assignments: validAssignments,
      email: formData.email?.trim() ? formData.email.trim() : null
    };
    try {
      if (editingLead) {
        await leadService.update(editingLead.id, payload as UpdateLeadData);
      } else {
        await leadService.create(payload);
      }
      setShowModal(false);
      setEditingLead(null);
      setFormData({
        party_name: '',
        contact_no: '',
        secondary_contact_no: '',
        secondary_contact_person: '',
        email: '',
        assignments: [defaultLeadAssignmentRow()]
      });
      loadLeads();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to save data';
      setModalError(msg);
    }
  };

  const handleEdit = async (lead: Lead) => {
    try {
      const fullLead = await leadService.getById(lead.id);
      setEditingLead(fullLead);
      const assignments: LeadAssignmentInput[] = (fullLead.assignments && fullLead.assignments.length > 0)
        ? fullLead.assignments.map((a) => ({
            brand_id: a.brand_id ?? undefined,
            user_id: a.user_id,
            status: (a.status as LeadAssignmentInput['status']) || 'PROSPECT'
          }))
        : [{
            brand_id: lead.brand_id ?? undefined,
            user_id: lead.assigned_to ?? 0,
            status: (lead.status as LeadAssignmentInput['status']) || 'PROSPECT'
          }];
      if (assignments.length === 0) {
        assignments.push(defaultLeadAssignmentRow());
      }
      setFormData({
        party_name: fullLead.party_name,
        contact_no: fullLead.contact_no,
        secondary_contact_no: fullLead.secondary_contact_no,
        office_address: fullLead.office_address,
        project_district: fullLead.project_district,
        project_location: fullLead.project_location,
        work_details: fullLead.work_details,
        contact_person: fullLead.contact_person,
        secondary_contact_person: fullLead.secondary_contact_person,
        email: fullLead.email ?? '',
        product_req: fullLead.product_req,
        qty_req: fullLead.qty_req,
        brands_in_use: fullLead.brands_in_use,
        price: fullLead.price,
        source: fullLead.source,
        assignments
      });
      setModalError('');
      setShowModal(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load lead');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this data?')) {
      return;
    }
    try {
      await leadService.delete(id);
      loadLeads();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete data');
    }
  };

  const handleUnassignAssignment = async (leadId: number, row: LeadAssignmentRow) => {
    if (!row.id) return;
    const hint = [row.brand_name || 'No brand', row.assigned_to_name].filter(Boolean).join(' → ');
    if (!window.confirm(`Remove this assignment${hint ? ` (${hint})` : ''}?`)) return;
    setError('');
    try {
      await leadService.deleteAssignment(leadId, row.id);
      setExpandedLeadIds((prev) => {
        if (!prev[leadId]) return prev;
        const next = { ...prev };
        delete next[leadId];
        return next;
      });
      await loadLeads();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to remove assignment');
    }
  };

  const handleSearch = () => {
    setPage(1);
    setSelectedLeadIds([]);
    setFilters({ ...filters, search: searchTerm });
  };

  const toggleLeadSelection = (leadId: number) => {
    setSelectedLeadIds((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  const toggleSelectAllCurrentPage = () => {
    const currentLeadIds = leads.map((l) => l.id);
    const allSelected = currentLeadIds.length > 0 && currentLeadIds.every((id) => selectedLeadIds.includes(id));
    if (allSelected) {
      setSelectedLeadIds((prev) => prev.filter((id) => !currentLeadIds.includes(id)));
    } else {
      setSelectedLeadIds((prev) => Array.from(new Set([...prev, ...currentLeadIds])));
    }
  };

  const clearBulkSelection = () => {
    setSelectedLeadIds([]);
    setBulkAssignments([{ brand_id: undefined as any, user_id: 0 }]);
    setBulkStatus('PROSPECT');
    setBulkSource('');
  };

  const runBulkAction = async () => {
    if (!canUseBulkActions || selectedLeadIds.length === 0 || bulkLoading) return;
    setError('');

    try {
      setBulkLoading(true);
      let result;

      if (bulkAction === 'delete') {
        if (!window.confirm(`Delete ${selectedLeadIds.length} selected lead(s)?`)) {
          return;
        }
        result = await leadService.bulkDelete(selectedLeadIds);
      } else if (bulkAction === 'assign') {
        const validAssignments = bulkAssignments
          .filter((a) => a.user_id && a.user_id > 0)
          .map((a) => ({
            brand_id: a.brand_id,
            user_id: a.user_id,
            status: bulkStatus,
          }));
        if (validAssignments.length === 0) {
          setError('At least one assignment (brand and user) is required for bulk assign');
          return;
        }
        result = await leadService.bulkAssign(selectedLeadIds, validAssignments);
      } else if (bulkAction === 'unassign') {
        const validUnassign = bulkAssignments.filter((a) => a.user_id && a.user_id > 0);
        if (validUnassign.length === 0) {
          setError('At least one brand/user row is required for bulk unassign');
          return;
        }
        if (!window.confirm(
          `Remove ${validUnassign.length} assignment row(s) from each of ${selectedLeadIds.length} selected lead(s) where they exist?`
        )) {
          return;
        }
        result = await leadService.bulkUnassign(selectedLeadIds, validUnassign);
      } else if (bulkAction === 'status') {
        if (!bulkStatus) {
          setError('Status is required');
          return;
        }
        result = await leadService.bulkStatus(selectedLeadIds, bulkStatus as any);
      } else {
        if (!bulkSource.trim()) {
          setError('Source is required');
          return;
        }
        result = await leadService.bulkSource(selectedLeadIds, bulkSource.trim());
      }

      const errorSummary = result.errors && result.errors.length > 0
        ? ` (${result.errors.length} failed)`
        : '';
      setError(`Bulk action complete: ${result.updated_count}/${result.total_selected} updated${errorSummary}`);
      clearBulkSelection();
      await loadLeads();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Bulk action failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'INTERSTED': return 'bg-green-100 text-green-800 border-green-300';
      case 'NOT INTERSTED': return 'bg-red-100 text-red-800 border-red-300';
      case 'PROSPECT': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'EXISTING OTHERS': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'EXISTING INFRASOL': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'customer': return 'bg-purple-100 text-purple-800 border-purple-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const isCallDue = (nextCallDate?: string) => {
    if (!nextCallDate) return false;
    const nextCall = new Date(nextCallDate);
    const now = new Date();
    return nextCall <= now;
  };

  const isCallToday = (nextCallDate?: string) => {
    if (!nextCallDate) return false;
    const nextCall = new Date(nextCallDate);
    const today = new Date();
    return nextCall.toDateString() === today.toDateString();
  };

  const formatLastCallOutcomeLabel = (raw?: string | null) => {
    if (!raw || !String(raw).trim()) return '';
    return String(raw)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const renderLastOutcomeCell = (outcome?: string | null) => {
    if (!outcome || !String(outcome).trim()) {
      return <span className="text-gray-400">-</span>;
    }
    const label = formatLastCallOutcomeLabel(outcome);
    const colorKey = String(outcome).trim().toLowerCase();
    return (
      <span
        className={`px-1.5 sm:px-2 inline-flex text-xs leading-4 sm:leading-5 font-medium rounded-full border max-w-[10rem] truncate ${getCallOutcomeColor(colorKey)}`}
        title={label}
      >
        {label}
      </span>
    );
  };

  const listRestore = { page, perPage, filters: { ...filters } };

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Data Management</h1>
          <div className="flex gap-2 w-full sm:w-auto">
            {user?.role === 'org_admin' && (
              <button
                onClick={async () => {
                  try {
                    await exportService.export('leads', filters);
                  } catch (err: any) {
                    setError(err.response?.data?.error || 'Failed to export data');
                  }
                }}
                className="bg-green-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-green-700 text-sm sm:text-base font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <Icon component={FiDownload} className="w-4 h-4" />
                <span>Export</span>
              </button>
            )}
            <button
              onClick={() => {
                setEditingLead(null);
                setModalError('');
                setFormData({
                  party_name: '',
                  contact_no: '',
                  secondary_contact_no: '',
                  secondary_contact_person: '',
                  email: '',
                  assignments: [defaultLeadAssignmentRow()]
                });
                setShowModal(true);
              }}
              className="bg-yellow-500 text-black-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-yellow-600 text-sm sm:text-base font-medium w-full sm:w-auto font-bold"
            >
              Add Data
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-10 gap-2 sm:gap-3 items-end">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status || ''}
                onChange={(e) => { setPage(1); setFilters({ ...filters, status: e.target.value as any || undefined }); }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Status</option>
                <option value="PROSPECT">PROSPECT</option>
                <option value="INTERSTED">INTERSTED</option>
                <option value="NOT INTERSTED">NOT INTERSTED</option>
                <option value="EXISTING OTHERS">EXISTING OTHERS</option>
                <option value="EXISTING INFRASOL">EXISTING INFRASOL</option>
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Brand</label>
              <select
                value={filters.brand_id ?? ''}
                onChange={(e) => { setPage(1); setFilters({ ...filters, brand_id: e.target.value ? parseInt(e.target.value) : undefined }); }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Brands</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Assigned To</label>
              <select
                value={filters.assigned_to || ''}
                onChange={(e) => { setPage(1); setFilters({ ...filters, assigned_to: e.target.value ? parseInt(e.target.value) : undefined }); }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Users</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Source</label>
              <select
                value={filters.source_empty ? '__EMPTY__' : (filters.source || '')}
                onChange={(e) => {
                  setPage(1);
                  const v = e.target.value;
                  if (v === '__EMPTY__') {
                    setFilters({ ...filters, source: undefined, source_empty: true });
                  } else if (v === '') {
                    setFilters({ ...filters, source: undefined, source_empty: undefined });
                  } else {
                    setFilters({ ...filters, source: v, source_empty: undefined });
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All sources</option>
                <option value="__EMPTY__">No Source</option>
                {leadListFilterOptions.sources.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Last call outcome</label>
              <select
                value={filters.last_call_outcome || ''}
                onChange={(e) => { setPage(1); setFilters({ ...filters, last_call_outcome: e.target.value || undefined }); }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All outcomes</option>
                <option value="_none">No outcome / no calls</option>
                {leadListFilterOptions.call_outcomes.map((o) => (
                  <option key={o} value={o}>{formatLastCallOutcomeLabel(o)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Project District</label>
              <input
                type="text"
                value={filters.project_district || ''}
                onChange={(e) => { setPage(1); setFilters({ ...filters, project_district: e.target.value || undefined }); }}
                placeholder="Filter by district"
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call</label>
              <select
                value={filters.next_call_due || ''}
                onChange={(e) => { setPage(1); setFilters({ ...filters, next_call_due: e.target.value as any || undefined }); }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All</option>
                <option value="today">Today</option>
                <option value="overdue">Overdue</option>
                <option value="upcoming">Upcoming (7 days)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call from</label>
              <input
                type="date"
                value={filters.call_date_from || ''}
                onChange={(e) => {
                  setPage(1);
                  setFilters(normalizeCallDateRange({ ...filters }, 'call_date_from', e.target.value || undefined));
                }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call to</label>
              <input
                type="date"
                value={filters.call_date_to || ''}
                onChange={(e) => {
                  setPage(1);
                  setFilters(normalizeCallDateRange({ ...filters }, 'call_date_to', e.target.value || undefined));
                }}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Name, contact..."
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="flex gap-2 flex-nowrap">
              <button
                onClick={handleSearch}
                className="bg-yellow-500 text-black-950 px-3 py-1.5 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold whitespace-nowrap"
              >
                Search
              </button>
              <button
                onClick={() => {
                  setPage(1);
                  setFilters({});
                  setSearchTerm('');
                  clearBulkSelection();
                }}
                className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-300 text-sm font-medium whitespace-nowrap"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-6 text-sm text-gray-600">Loading...</div>
        ) : (
          <div className="bg-white shadow-sm overflow-hidden rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {canUseBulkActions && (
                      <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={leads.length > 0 && leads.every((l) => selectedLeadIds.includes(l.id))}
                          onChange={toggleSelectAllCurrentPage}
                          aria-label="Select all leads on this page"
                        />
                      </th>
                    )}
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Party Name
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      Contact
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Email
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      Converted
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                      Brand
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Assigned To
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Calls
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                      Last outcome
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Next Call
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leads.map((lead) => {
                    const assignments: LeadAssignmentRow[] =
                      lead.assignments && lead.assignments.length > 0
                        ? lead.assignments
                        : (() => {
                            const bid = lead.brand_id ?? null;
                            const uid = lead.assigned_to ?? 0;
                            const flags = syntheticAssignmentConversionFlags(lead, bid, uid);
                            return [
                              {
                                id: lead.assignment_id ?? 0,
                                lead_id: lead.id,
                                brand_id: bid,
                                brand_name: lead.brand_name,
                                user_id: uid,
                                assigned_to_name: lead.assigned_to_name,
                                status: lead.status,
                                last_call_date: lead.last_call_date,
                                next_call_date: lead.next_call_date,
                                total_calls: lead.total_calls ?? 0,
                                last_call_outcome: lead.last_call_outcome ?? null,
                                brand_converted: flags.brand_converted,
                                converted_by_this_user: flags.converted_by_this_user
                              }
                            ];
                          })();
                    const remainingAssignments = assignments.slice(1);
                    const isExpanded = !!expandedLeadIds[lead.id];
                    const extraAssignmentCount = remainingAssignments.length;
                    const toggleExpanded = () => {
                      setExpandedLeadIds((prev) => ({ ...prev, [lead.id]: !prev[lead.id] }));
                    };

                    return (
                      <React.Fragment key={lead.id}>
                        <tr key={lead.id} className="hover:bg-gray-50">
                    {canUseBulkActions && (
                        <td className="px-2 sm:px-3 py-1.5 text-xs">
                          <input
                            type="checkbox"
                            checked={selectedLeadIds.includes(lead.id)}
                            onChange={() => toggleLeadSelection(lead.id)}
                            aria-label={`Select lead ${displayPartyName(lead.party_name)}`}
                          />
                        </td>
                      )}
                      <td className="px-2 sm:px-3 py-1.5 text-xs font-medium text-gray-900">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Link to={`/leads/${lead.id}`} state={{ assignmentId: lead.assignment_id, brandId: lead.brand_id, listRestore }} className="text-yellow-600 hover:text-yellow-700">
                            {displayPartyName(lead.party_name)}
                          </Link>
                          {assignments[0]?.converted_by_this_user && (
                            <span className="sm:hidden inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium" title={conversionBadgeTooltip(lead, { brand_id: assignments[0]?.brand_id ?? null, user_id: assignments[0]?.user_id ?? 0 })}>
                              <Icon component={FiCheckCircle} className="w-3 h-3" />
                              Converted
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500 hidden sm:table-cell">
                        <div className="flex items-center space-x-1">
                          <Icon component={FiPhone} className="w-3 h-3" />
                          <span>{lead.contact_no}</span>
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500 hidden md:table-cell max-w-[10rem] truncate" title={lead.email || undefined}>
                        {lead.email ? (
                          <a href={`mailto:${lead.email}`} className="text-yellow-700 hover:underline flex items-center gap-1 min-w-0">
                            <Icon component={FiMail} className="w-3 h-3 shrink-0" />
                            <span className="truncate">{lead.email}</span>
                          </a>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 sm:px-3 py-1.5">
                        <span className={`px-1.5 sm:px-2 inline-flex text-xs leading-4 sm:leading-5 font-semibold rounded-full border ${getStatusColor(lead.status)}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs hidden sm:table-cell">
                        {assignments[0]?.converted_by_this_user ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300 text-xs font-medium" title={conversionBadgeTooltip(lead, { brand_id: assignments[0]?.brand_id ?? null, user_id: assignments[0]?.user_id ?? 0 })}>
                            <Icon component={FiCheckCircle} className="w-3.5 h-3.5" />
                            Converted
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500 hidden lg:table-cell">
                        {lead.brand_name ?? '-'}
                      </td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500 hidden md:table-cell">
                        {lead.assigned_to_name || '-'}
                      </td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Icon component={FiPhone} className="w-3 h-3" />
                          <span>{lead.total_calls || 0}</span>
                        </div>
                        {lead.last_call_date && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            {formatDate(lead.last_call_date)}
                          </div>
                        )}
                      </td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs hidden lg:table-cell max-w-[8rem]">
                        {renderLastOutcomeCell(lead.last_call_outcome ?? null)}
                      </td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs">
                        {lead.next_call_date ? (
                          <div className={`flex items-center space-x-1 ${
                            isCallDue(lead.next_call_date) ? 'text-red-600' : 
                            isCallToday(lead.next_call_date) ? 'text-orange-600' : 
                            'text-gray-600'
                          }`}>
                            <Icon component={isCallDue(lead.next_call_date) ? FiClock : FiCalendar} className="w-3 h-3" />
                            <span>{formatDateTime(lead.next_call_date)}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-2 sm:px-3 py-1.5 whitespace-nowrap text-xs font-medium">
                        {extraAssignmentCount > 0 && (
                          <button
                            type="button"
                            onClick={toggleExpanded}
                            aria-expanded={isExpanded}
                            title={
                              isExpanded
                                ? 'Hide extra assignments'
                                : `Show ${extraAssignmentCount} more assignment${extraAssignmentCount === 1 ? '' : 's'}`
                            }
                            aria-label={
                              isExpanded
                                ? 'Collapse extra assignments'
                                : `Expand ${extraAssignmentCount} more assignment${extraAssignmentCount === 1 ? '' : 's'}`
                            }
                            className="bg-orange-500 text-white rounded-full px-1.5 py-0.5 text-[11px] font-semibold hover:bg-orange-600 mr-1.5 sm:mr-3"
                          >
                            {extraAssignmentCount}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openCallLogModal(lead)}
                          title="Call Log"
                          aria-label="Call Log"
                          className="text-yellow-600 hover:text-yellow-700 mr-1.5 sm:mr-3"
                        >
                          <Icon component={FiCalendar} className="w-3.5 h-3.5" />
                        </button>
                        {canUseBulkActions && assignments[0]?.id > 0 && !assignments[0]?.brand_converted && (
                          <button
                            type="button"
                            onClick={() => handleUnassignAssignment(lead.id, assignments[0])}
                            title="Un-assign (remove this brand/user assignment)"
                            aria-label="Un-assign"
                            className="text-orange-600 hover:text-orange-800 mr-1.5 sm:mr-3"
                          >
                            <Icon component={FiUserMinus} className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {hasPermission('leads', 'edit') && (
                          <button
                            onClick={() => handleEdit(lead)}
                            title="Edit"
                            aria-label="Edit"
                            className="text-yellow-600 hover:text-yellow-700 mr-1.5 sm:mr-3"
                          >
                            <Icon component={FiEdit2} className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {hasPermission('leads', 'delete') && !leadHasAnyConversion(lead) && (
                          <button
                            onClick={() => handleDelete(lead.id)}
                            title="Delete"
                            aria-label="Delete"
                            className="text-red-600 hover:text-red-900"
                          >
                            <Icon component={FiTrash2} className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && remainingAssignments.map((assignment) => (
                      <tr key={assignment.id} className="bg-gray-50 hover:bg-gray-100">
                        {canUseBulkActions && (
                          <td className="px-2 sm:px-3 py-1.5" />
                        )}
                        <td className="px-2 sm:px-3 py-1.5 text-xs font-medium text-gray-900">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Link
                              to={`/leads/${lead.id}`}
                              state={{ assignmentId: assignment.id, brandId: assignment.brand_id ?? undefined, listRestore }}
                              className="text-yellow-600 hover:text-yellow-700"
                            >
                              {displayPartyName(lead.party_name)}
                            </Link>
                            {assignment.converted_by_this_user && (
                              <span className="sm:hidden inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium" title={conversionBadgeTooltip(lead, assignment)}>
                                <Icon component={FiCheckCircle} className="w-3 h-3" />
                                Converted
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500 hidden sm:table-cell">
                          <div className="flex items-center space-x-1">
                            <Icon component={FiPhone} className="w-3 h-3" />
                            <span>{lead.contact_no}</span>
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500 hidden md:table-cell max-w-[10rem] truncate" title={lead.email || undefined}>
                          {lead.email ? (
                            <a href={`mailto:${lead.email}`} className="text-yellow-700 hover:underline flex items-center gap-1 min-w-0">
                              <Icon component={FiMail} className="w-3 h-3 shrink-0" />
                              <span className="truncate">{lead.email}</span>
                            </a>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5">
                          <span
                            className={`px-1.5 sm:px-2 inline-flex text-xs leading-4 sm:leading-5 font-semibold rounded-full border ${getStatusColor(assignment.status)}`}
                          >
                            {assignment.status}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 text-xs hidden sm:table-cell">
                          {assignment.converted_by_this_user ? (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300 text-xs font-medium"
                              title={conversionBadgeTooltip(lead, assignment)}
                            >
                              <Icon component={FiCheckCircle} className="w-3.5 h-3.5" />
                              Converted
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500 hidden lg:table-cell">
                          {assignment.brand_name ?? '-'}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500 hidden md:table-cell">
                          {assignment.assigned_to_name || '-'}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Icon component={FiPhone} className="w-3 h-3" />
                            <span>{assignment.total_calls || 0}</span>
                          </div>
                          {assignment.last_call_date && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {formatDate(assignment.last_call_date)}
                            </div>
                          )}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 text-xs hidden lg:table-cell max-w-[8rem]">
                          {renderLastOutcomeCell(assignment.last_call_outcome ?? null)}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 text-xs">
                          {assignment.next_call_date ? (
                            <div
                              className={`flex items-center space-x-1 ${
                                isCallDue(assignment.next_call_date) ? 'text-red-600' :
                                isCallToday(assignment.next_call_date) ? 'text-orange-600' :
                                'text-gray-600'
                              }`}
                            >
                              <Icon
                                component={isCallDue(assignment.next_call_date) ? FiClock : FiCalendar}
                                className="w-3 h-3"
                              />
                              <span>{formatDateTime(assignment.next_call_date)}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-2 sm:px-3 py-1.5 whitespace-nowrap text-xs font-medium">
                          <button
                            type="button"
                            onClick={() => openCallLogModal(lead, assignment)}
                            title="Call Log"
                            aria-label="Call Log"
                            className="text-yellow-600 hover:text-yellow-700 mr-1.5 sm:mr-3"
                          >
                            <Icon component={FiCalendar} className="w-3.5 h-3.5" />
                          </button>
                          {canUseBulkActions && assignment.id > 0 && !assignment.brand_converted && (
                            <button
                              type="button"
                              onClick={() => handleUnassignAssignment(lead.id, assignment)}
                              title="Un-assign (remove this brand/user assignment)"
                              aria-label="Un-assign"
                              className="text-orange-600 hover:text-orange-800 mr-1.5 sm:mr-3"
                            >
                              <Icon component={FiUserMinus} className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {leads.length === 0 && !loading && (
              <div className="text-center py-8 text-sm text-gray-500">No data found</div>
            )}
            <ListPagination
              page={page}
              perPage={perPage}
              total={total}
              onPageChange={setPage}
              onPerPageChange={setPerPage}
            />
          </div>
        )}

        {canUseBulkActions && (
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex flex-col lg:flex-row lg:items-end gap-3">
              <div className="text-sm text-gray-700 min-w-[150px]">
                Selected: <span className="font-semibold">{selectedLeadIds.length}</span>
              </div>
              <div className="min-w-[180px]">
                <div className="flex items-center gap-2">
                  <label className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">Bulk Action</label>
                  <select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value as any)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  >
                    <option value="delete">Delete Leads</option>
                    <option value="assign">Assign Brand/User</option>
                    <option value="unassign">Un-Assign Brand/User</option>
                    <option value="status">Change Status</option>
                    <option value="source">Change Source</option>
                  </select>
                </div>
              </div>

              {(bulkAction === 'assign' || bulkAction === 'unassign') && (
                <div className="w-full flex flex-col gap-2 lg:max-w-2xl">
                  <label className="text-xs sm:text-sm font-medium text-gray-700">
                    {bulkAction === 'assign'
                      ? 'Assignments (brand + user)'
                      : 'Remove these assignments (brand + user) from each selected lead'}
                  </label>
                  <div className="space-y-2">
                    {bulkAssignments.map((a, idx) => (
                      <div key={idx} className="flex flex-wrap gap-2 items-center">
                        <select
                          value={a.brand_id ?? ''}
                          onChange={(e) => {
                            const brandId = e.target.value ? parseInt(e.target.value) : undefined;
                            setBulkAssignments((prev) => {
                              const next = [...prev];
                              next[idx] = { ...next[idx], brand_id: brandId };
                              return next;
                            });
                          }}
                          className="min-w-[9rem] flex-1 sm:flex-none border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                        >
                          <option value="">No brand</option>
                          {brands.map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                        <select
                          value={a.user_id || ''}
                          onChange={(e) => {
                            const userId = e.target.value ? parseInt(e.target.value) : 0;
                            setBulkAssignments((prev) => {
                              const next = [...prev];
                              next[idx] = { ...next[idx], user_id: userId };
                              return next;
                            });
                          }}
                          className="min-w-[9rem] flex-1 sm:flex-none border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                        >
                          <option value="">Select user</option>
                          {users.map((u) => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => {
                            setBulkAssignments((prev) => {
                              const next = prev.filter((_, i) => i !== idx);
                              return next.length > 0 ? next : [{ brand_id: undefined as any, user_id: 0 }];
                            });
                          }}
                          className="text-red-600 hover:text-red-800 p-1"
                          aria-label="Remove assignment row"
                        >
                          <Icon component={FiX} className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setBulkAssignments((prev) => [...prev, { brand_id: undefined as any, user_id: 0 }])}
                      className="text-yellow-700 hover:text-yellow-800 text-xs sm:text-sm font-medium"
                    >
                      + Add row
                    </button>
                  </div>
                  {bulkAction === 'assign' && (
                    <div className="min-w-[180px] max-w-xs">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status for these assignments</label>
                      <select
                        value={bulkStatus || ''}
                        onChange={(e) => setBulkStatus(e.target.value as any)}
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      >
                        <option value="PROSPECT">PROSPECT</option>
                        <option value="INTERSTED">INTERSTED</option>
                        <option value="NOT INTERSTED">NOT INTERSTED</option>
                        <option value="EXISTING OTHERS">EXISTING OTHERS</option>
                        <option value="EXISTING INFRASOL">EXISTING INFRASOL</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {bulkAction === 'status' && (
                <div className="min-w-[180px]">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={bulkStatus || ''}
                    onChange={(e) => setBulkStatus(e.target.value as any)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  >
                    <option value="PROSPECT">PROSPECT</option>
                    <option value="INTERSTED">INTERSTED</option>
                    <option value="NOT INTERSTED">NOT INTERSTED</option>
                    <option value="EXISTING OTHERS">EXISTING OTHERS</option>
                    <option value="EXISTING INFRASOL">EXISTING INFRASOL</option>
                  </select>
                </div>
              )}

              {bulkAction === 'source' && (
                <div className="min-w-[220px]">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Source</label>
                  <input
                    type="text"
                    value={bulkSource}
                    onChange={(e) => setBulkSource(e.target.value)}
                    placeholder="Enter source"
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                  />
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={selectedLeadIds.length === 0 || bulkLoading}
                  onClick={runBulkAction}
                  className="bg-yellow-500 text-black-950 px-3 py-1.5 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkLoading ? 'Applying...' : 'Apply'}
                </button>
                <button
                  type="button"
                  onClick={clearBulkSelection}
                  className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-300 text-sm font-medium"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-2 sm:p-3">
            <div className="relative top-2 sm:top-6 mx-auto p-3 border w-full max-w-5xl shadow-lg rounded-md bg-white max-h-[96vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-900">
                  {editingLead ? 'Edit lead' : 'Add lead'}
                </h3>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingLead(null); setModalError(''); }}
                  className="text-gray-400 hover:text-gray-600 p-0.5 -mr-0.5"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-4 h-4" />
                </button>
              </div>
              {modalError && (
                <div className="mb-2 bg-red-50 border border-red-200 text-red-800 px-2.5 py-1.5 rounded text-xs">
                  {modalError}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-1.5">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-x-2 gap-y-1.5">
                  <div className="col-span-2 sm:col-span-2 md:col-span-2 xl:col-span-2">
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Party name *</label>
                    <input
                      type="text"
                      required
                      value={formData.party_name}
                      onChange={(e) => setFormData({ ...formData, party_name: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 md:col-span-1 xl:col-span-2">
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Source</label>
                    <input
                      type="text"
                      value={formData.source || ''}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                      placeholder="e.g. Portal, Reference"
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-3 md:col-span-4 xl:col-span-6">
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Assignments (brand, user, status) *</label>
                    <div className="space-y-1">
                      {(formData.assignments || []).map((a, idx) => (
                        <div key={idx} className="flex flex-wrap gap-1.5 items-center">
                          <select
                            value={a.brand_id ?? ''}
                            onChange={(e) => {
                              const next = [...(formData.assignments || [])];
                              next[idx] = { ...next[idx], brand_id: e.target.value ? parseInt(e.target.value) : undefined };
                              setFormData({ ...formData, assignments: next });
                            }}
                            className="border border-gray-300 rounded px-1.5 py-0.5 text-xs min-w-[7rem] flex-1 sm:flex-none sm:min-w-[8rem]"
                          >
                            <option value="">Brand</option>
                            {brands.map((b) => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                          </select>
                          <select
                            value={a.user_id || ''}
                            onChange={(e) => {
                              const next = [...(formData.assignments || [])];
                              next[idx] = { ...next[idx], user_id: parseInt(e.target.value) || 0 };
                              setFormData({ ...formData, assignments: next });
                            }}
                            className="border border-gray-300 rounded px-1.5 py-0.5 text-xs min-w-[7rem] flex-1 sm:flex-none sm:min-w-[9rem]"
                          >
                            <option value="">User</option>
                            {users.map((u) => (
                              <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                          </select>
                          <select
                            value={
                              a.status && LEAD_ASSIGNMENT_STATUS_OPTIONS.includes(a.status as NonNullable<LeadAssignmentInput['status']>)
                                ? a.status
                                : 'PROSPECT'
                            }
                            onChange={(e) => {
                              const next = [...(formData.assignments || [])];
                              next[idx] = { ...next[idx], status: e.target.value as LeadAssignmentInput['status'] };
                              setFormData({ ...formData, assignments: next });
                            }}
                            className="border border-gray-300 rounded px-1.5 py-0.5 text-xs min-w-[6.5rem] flex-1 sm:flex-none sm:min-w-[7.5rem]"
                            aria-label="Assignment status"
                          >
                            {LEAD_ASSIGNMENT_STATUS_OPTIONS.map((st) => (
                              <option key={st} value={st}>{st}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => {
                              const next = (formData.assignments || []).filter((_, i) => i !== idx);
                              setFormData({ ...formData, assignments: next.length ? next : [defaultLeadAssignmentRow()] });
                            }}
                            className="text-red-600 hover:text-red-800 p-0.5"
                            aria-label="Remove assignment"
                          >
                            <Icon component={FiX} className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, assignments: [...(formData.assignments || []), defaultLeadAssignmentRow()] })}
                        className="text-yellow-700 hover:text-yellow-800 text-xs font-medium"
                      >
                        + Add row
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Contact person</label>
                    <input
                      type="text"
                      value={formData.contact_person || ''}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Contact no *</label>
                    <input
                      type="tel"
                      required
                      value={formData.contact_no}
                      onChange={(e) => setFormData({ ...formData, contact_no: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">2nd contact</label>
                    <input
                      type="text"
                      value={formData.secondary_contact_person || ''}
                      onChange={(e) => setFormData({ ...formData, secondary_contact_person: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">2nd no</label>
                    <input
                      type="tel"
                      value={formData.secondary_contact_no || ''}
                      onChange={(e) => setFormData({ ...formData, secondary_contact_no: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Email</label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">District</label>
                    <input
                      type="text"
                      value={formData.project_district || ''}
                      onChange={(e) => setFormData({ ...formData, project_district: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-2 md:col-span-2 xl:col-span-2">
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Project location</label>
                    <input
                      type="text"
                      value={formData.project_location || ''}
                      onChange={(e) => setFormData({ ...formData, project_location: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Product</label>
                    <input
                      type="text"
                      value={formData.product_req || ''}
                      onChange={(e) => setFormData({ ...formData, product_req: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Qty</label>
                    <input
                      type="text"
                      value={formData.qty_req || ''}
                      onChange={(e) => setFormData({ ...formData, qty_req: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price || ''}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value ? parseFloat(e.target.value) : undefined })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-2 md:col-span-2 xl:col-span-2">
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Brands in use</label>
                    <input
                      type="text"
                      value={formData.brands_in_use || ''}
                      onChange={(e) => setFormData({ ...formData, brands_in_use: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-3 md:col-span-4 xl:col-span-6">
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Office address</label>
                    <textarea
                      value={formData.office_address || ''}
                      onChange={(e) => setFormData({ ...formData, office_address: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug resize-y min-h-[2rem]"
                      rows={2}
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-3 md:col-span-4 xl:col-span-6">
                    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">Work details</label>
                    <textarea
                      value={formData.work_details || ''}
                      onChange={(e) => setFormData({ ...formData, work_details: e.target.value })}
                      className="block w-full border border-gray-300 rounded px-2 py-1 text-xs leading-snug resize-y min-h-[2rem]"
                      rows={2}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-1.5 pt-1.5 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingLead(null);
                      setModalError('');
                    }}
                    className="px-2.5 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-xs font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-2.5 py-1 bg-yellow-500 text-black-950 rounded hover:bg-yellow-600 text-xs font-bold"
                  >
                    {editingLead ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Call Log Modal */}
        {callLogModalLead && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  Call Log – {displayPartyName(callLogModalLead.party_name)}
                  {callLogModalLead.assigned_to_name && (
                    <span className="font-normal text-gray-600 ml-1">(Assigned: {callLogModalLead.assigned_to_name})</span>
                  )}
                </h3>
                <button
                  type="button"
                  onClick={closeCallLogModal}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>

              <PriorCallAlert entityType="lead" entityId={callLogModalLead.id} />

              {showCallLogForm ? (
                <form onSubmit={handleCallLogSubmit} className="space-y-3 sm:space-y-4">
                  <AllCallLogButton
                    entityType="lead"
                    entityId={callLogModalLead.id}
                    entityName={displayPartyName(callLogModalLead.party_name)}
                  />
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Date *</label>
                      <input
                        type="date"
                        required
                        value={callFormData.call_date}
                        onChange={(e) => setCallFormData({ ...callFormData, call_date: e.target.value })}
                        className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Time</label>
                      <input
                        type="time"
                        value={callFormData.call_time || ''}
                        onChange={(e) => setCallFormData({ ...callFormData, call_time: e.target.value })}
                        className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Status *</label>
                    <CallStatusSelect
                      required
                      value={callFormData.call_status_id}
                      onChange={(call_status_id) => setCallFormData({ ...callFormData, call_status_id })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Call Outcome *</label>
                    <select
                      required
                      value={callFormData.call_outcome || ''}
                      onChange={(e) => setCallFormData({ ...callFormData, call_outcome: e.target.value || undefined })}
                      disabled={callOutcomeOptionsLoading}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm disabled:opacity-60"
                    >
                      <option value="">{callOutcomeOptionsLoading ? 'Loading…' : 'Select Outcome'}</option>
                      {callOutcomeOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={callFormData.notes || ''}
                      onChange={(e) => setCallFormData({ ...callFormData, notes: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      rows={3}
                      placeholder="Enter call notes..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Next Call Date</label>
                    <input
                      type="datetime-local"
                      value={callFormData.next_call_date ? (() => {
                        const date = new Date(callFormData.next_call_date);
                        const y = date.getFullYear();
                        const m = String(date.getMonth() + 1).padStart(2, '0');
                        const d = String(date.getDate()).padStart(2, '0');
                        const h = String(date.getHours()).padStart(2, '0');
                        const min = String(date.getMinutes()).padStart(2, '0');
                        return `${y}-${m}-${d}T${h}:${min}`;
                      })() : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          setCallFormData({ ...callFormData, next_call_date: datetimeLocalToMysql(e.target.value) });
                        } else {
                          setCallFormData({ ...callFormData, next_call_date: undefined });
                        }
                      }}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button type="button" onClick={() => setShowCallLogForm(false)} className="px-3 py-1.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancel</button>
                    <button type="submit" className="px-3 py-1.5 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold">Log Call</button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="flex justify-end mb-3">
                    <button
                      type="button"
                      onClick={() => setShowCallLogForm(true)}
                      className="px-3 py-1.5 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold flex items-center gap-1"
                    >
                      <Icon component={FiPhone} className="w-4 h-4" />
                      Log New Call
                    </button>
                  </div>
                  {callLogLoading ? (
                    <div className="text-center py-6 text-sm text-gray-500">Loading call log...</div>
                  ) : callLogs.length === 0 ? (
                    <div className="text-center py-6 text-sm text-gray-500">No call logs yet</div>
                  ) : (
                    <div className="space-y-4 max-h-[50vh] overflow-y-auto">
                      {callLogs.map((log) => (
                        <div key={log.id} className="border-l-4 border-indigo-500 pl-4 py-2">
                          <div className="flex flex-wrap justify-between items-start gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-900">
                              {formatDateTime(log.call_date, log.call_time)}
                            </span>
                            {log.called_by_name && (
                              <span className="text-xs text-gray-500">
                                by {log.called_by_name}
                                {log.logged_by_name && log.logged_by_name !== log.called_by_name && (
                                  <span className="text-gray-400"> (logged by {log.logged_by_name})</span>
                                )}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 mb-1">
                            {log.call_status && (
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${getSettingColorClass(log.call_status_color_code)} border`}>
                                {log.call_status}
                              </span>
                            )}
                            {log.call_outcome && (
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${getCallOutcomeColor(log.call_outcome)}`}>
                                {log.call_outcome}
                              </span>
                            )}
                          </div>
                          {log.notes && (
                            <div className="text-sm text-gray-700 mt-1 flex items-start space-x-2">
                              <Icon component={FiMessageSquare} className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                              <p className="flex-1">{log.notes}</p>
                            </div>
                          )}
                          {(log.next_call_date || log.next_call_notes) && (
                            <div className="mt-2 text-xs text-indigo-600">
                              {log.next_call_date && (
                                <div className="flex items-center space-x-1">
                                  <Icon component={FiCalendar} className="w-3 h-3" />
                                  <span>Next call: {formatDateTime(log.next_call_date)}</span>
                                </div>
                              )}
                              {log.next_call_notes && (
                                <p className="mt-0.5 pl-4 text-indigo-800">Next call notes: {log.next_call_notes}</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Leads;

